import type { ReactNode } from 'react'
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'

import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type ConfigUpdateProposal = Extract<
  InFlightAgentActionProposal,
  { kind: 'update_custom_agent_config' }
>

function isConfigUpdate(p: InFlightAgentActionProposal): p is ConfigUpdateProposal {
  return p.kind === 'update_custom_agent_config'
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span> {children}
    </div>
  )
}

function ProposalDetail({ proposal }: { proposal: ConfigUpdateProposal }) {
  const triggerCount = proposal.triggers?.length
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        {proposal.prompt !== undefined ? (
          <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background/60 p-2 font-mono text-10 leading-relaxed">
            {proposal.prompt}
          </pre>
        ) : null}
        {triggerCount !== undefined ? (
          <Field label="Triggers">
            {triggerCount === 1 ? '1 trigger' : `${triggerCount} triggers`}
          </Field>
        ) : null}
        {proposal.triggerEvents?.length ? (
          <Field label="Trigger events">
            <span className="font-mono">{proposal.triggerEvents.join(', ')}</span>
          </Field>
        ) : null}
        {proposal.cron !== undefined ? (
          <Field label="Cron">
            <span className="font-mono">{proposal.cron === '' ? '(cleared)' : proposal.cron}</span>
          </Field>
        ) : null}
        {proposal.triggerCooldownMs !== undefined ? (
          <Field label="Cooldown">
            <span className="font-mono">{proposal.triggerCooldownMs} ms</span>
          </Field>
        ) : null}
        {proposal.analysisWindowMs !== undefined ? (
          <Field label="Analysis window">
            <span className="font-mono">{proposal.analysisWindowMs} ms</span>
          </Field>
        ) : null}
        <div className="break-words text-muted-foreground">
          Approving PATCHes this agent. The output schema cannot change.
        </div>
      </div>
    </div>
  )
}

export const customAgentConfigUpdateViewCapability: AgentActionViewCapability = {
  kind: 'update_custom_agent_config',
  badgeLabel() {
    return 'agent config'
  },
  renderDetail(proposal) {
    if (!isConfigUpdate(proposal)) return null
    return <ProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isConfigUpdate(proposal)) return null
    if (proposal.terminalState?.kind === 'applied') {
      return (
        <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
          Applied — agent config updated.
        </p>
      )
    }
    if (proposal.terminalState?.kind === 'apply_failed') {
      return (
        <p className="px-1 text-10 text-destructive">
          Apply failed — {proposal.terminalState.error}
        </p>
      )
    }
    if (proposal.terminalState?.kind === 'rejected') {
      return (
        <p className="px-1 text-10 text-muted-foreground">
          Rejected — agent config left unchanged.
        </p>
      )
    }
    return null
  }
}

registerAgentActionView(customAgentConfigUpdateViewCapability)
